import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link as RouterLink, NavLink, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faBars,
  faChevronDown,
  faMagnifyingGlass,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

import logo from "../../images/AxiomosLogo.png";
import servicesData from "../../JsonFiles/Services.json";
import technologyData from "../../JsonFiles/Technologies.json";
import projectData from "../../JsonFiles/Projects.json";
import industriesData from "../../JsonFiles/Industries.json";
import "./MobileNav.css";

const MOBILE_BREAKPOINT = "(min-width: 1080px)";
const SWIPE_CLOSE_DISTANCE = 64;

const asText = (value, fallback = "") => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }

  return fallback;
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const buildChildren = (data, route, maximum = 7) => {
  const items = asArray(data)
    .slice(0, maximum)
    .map((item, index) => {
      const id = asText(item?.id ?? item?.slug ?? index + 1);
      const label = asText(item?.title ?? item?.name);

      if (!label) return null;

      return {
        label,
        to: `${route}/${encodeURIComponent(id)}`,
      };
    })
    .filter(Boolean);

  return items;
};

const NAVIGATION_ITEMS = [
  {
    label: "Case Studies",
    to: "/projects",
    description: "See how we turn complex ideas into useful products.",
    children: [
      { label: "View all case studies", to: "/projects" },
      ...buildChildren(projectData, "/projects", 5),
    ],
  },
  {
    label: "Services",
    to: "/services",
    description: "Digital product services designed around business outcomes.",
    children: [
      { label: "View all services", to: "/services" },
      ...buildChildren(servicesData, "/services", 7),
    ],
  },
  {
    label: "Technologies",
    to: "/technologies",
    description: "Modern platforms and tools selected for dependable delivery.",
    children: [
      { label: "View our technology stack", to: "/technologies" },
      ...buildChildren(technologyData, "/technologies", 8),
    ],
  },
  {
    label: "Industries",
    to: "/industries",
    description: "Industry-focused technology grounded in real operations.",
    children: [
      { label: "View all industries", to: "/industries" },
      ...buildChildren(industriesData, "/industries", 7),
    ],
  },
  {
    label: "Company",
    to: "/about",
    description: "Meet the people, principles, and purpose behind Axiomos.",
    children: [
      { label: "About Axiomos", to: "/about" },
      { label: "Careers", to: "/career" },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    label: "Blog",
    to: "/blog",
    description: "Ideas on software, products, and digital transformation.",
  },
];

const scrollAfterNavigation = (destination) => {
  const [, hash] = destination.split("#");
  const reduceMotion = window.matchMedia?.(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  const behavior = reduceMotion ? "auto" : "smooth";

  window.requestAnimationFrame(() => {
    if (hash) {
      window.setTimeout(() => {
        const target = document.getElementById(decodeURIComponent(hash));
        target?.scrollIntoView({ behavior, block: "start" });
      }, 60);
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior });
  });
};

const MobileNav = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [query, setQuery] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);

  const generatedId = useId().replace(/:/g, "");
  const menuId = `axiomos-mobile-menu-${generatedId}`;

  const menuRef = useRef(null);
  const toggleButtonRef = useRef(null);
  const searchInputRef = useRef(null);
  const touchStartX = useRef(null);

  const location = useLocation();
  const currentLocation = `${location.pathname}${location.hash}`;

  const filteredItems = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return NAVIGATION_ITEMS;

    return NAVIGATION_ITEMS.map((item) => {
      const parentMatches = [item.label, item.description]
        .join(" ")
        .toLowerCase()
        .includes(normalizedQuery);
      const matchingChildren = asArray(item.children).filter((child) =>
        child.label.toLowerCase().includes(normalizedQuery),
      );

      if (!parentMatches && matchingChildren.length === 0) return null;

      return {
        ...item,
        children: parentMatches ? item.children : matchingChildren,
      };
    }).filter(Boolean);
  }, [query]);

  const closeMenu = useCallback((restoreFocus = false) => {
    setIsOpen(false);
    setExpandedMenu(null);
    setQuery("");

    if (restoreFocus) {
      window.requestAnimationFrame(() => toggleButtonRef.current?.focus());
    }
  }, []);

  const openMenu = () => {
    setIsOpen(true);
    setExpandedMenu(null);
  };

  const toggleMenu = () => {
    if (isOpen) closeMenu(true);
    else openMenu();
  };

  const toggleSubmenu = (label) => {
    setExpandedMenu((currentMenu) => (currentMenu === label ? null : label));
  };

  const handleNavigation = useCallback(
    (destination) => {
      closeMenu(false);
      scrollAfterNavigation(destination);
    },
    [closeMenu],
  );

  useEffect(() => {
    setIsOpen(false);
    setExpandedMenu(null);
    setQuery("");
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const desktopQuery = window.matchMedia(MOBILE_BREAKPOINT);
    const handleBreakpointChange = (event) => {
      if (event.matches) closeMenu(false);
    };

    if (desktopQuery.addEventListener) {
      desktopQuery.addEventListener("change", handleBreakpointChange);
    } else {
      desktopQuery.addListener(handleBreakpointChange);
    }

    return () => {
      if (desktopQuery.removeEventListener) {
        desktopQuery.removeEventListener("change", handleBreakpointChange);
      } else {
        desktopQuery.removeListener(handleBreakpointChange);
      }
    };
  }, [closeMenu]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 12);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    const focusFrame = window.requestAnimationFrame(() => {
      menuRef.current?.querySelector(".axiomos-mobile-nav__close")?.focus();
    });

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeMenu(true);
        return;
      }

      if (event.key !== "Tab" || !menuRef.current) return;

      const focusableElements = Array.from(
        menuRef.current.querySelectorAll(
          'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"])',
        ),
      );

      if (!focusableElements.length) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, closeMenu]);

  const handleTouchStart = (event) => {
    touchStartX.current = event.touches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event) => {
    if (touchStartX.current === null) return;

    const endX = event.changedTouches[0]?.clientX ?? touchStartX.current;
    const distance = endX - touchStartX.current;

    if (distance > SWIPE_CLOSE_DISTANCE) closeMenu(false);
    touchStartX.current = null;
  };

  return (
    <nav
      className={`axiomos-mobile-nav${isScrolled ? " is-scrolled" : ""}`}
      aria-label="Mobile navigation"
    >
      <div className="axiomos-mobile-nav__bar">
        <RouterLink
          to="/"
          className="axiomos-mobile-nav__logo-link"
          aria-label="Axiomos home"
          onClick={() => handleNavigation("/")}
        >
          <img
            src={logo}
            alt="Axiomos Technologies"
            className="axiomos-mobile-nav__logo"
            decoding="async"
          />
        </RouterLink>

        <div className="axiomos-mobile-nav__bar-actions">
          <RouterLink
            to="/contact"
            className="axiomos-mobile-nav__bar-contact"
            onClick={() => handleNavigation("/contact")}
          >
            Letâ€™s talk
          </RouterLink>

          <button
            ref={toggleButtonRef}
            type="button"
            className="axiomos-mobile-nav__toggle"
            aria-label={
              isOpen ? "Close navigation menu" : "Open navigation menu"
            }
            aria-expanded={isOpen}
            aria-controls={menuId}
            onClick={toggleMenu}
          >
            <span>{isOpen ? "Close" : "Menu"}</span>
            <FontAwesomeIcon
              icon={isOpen ? faTimes : faBars}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      <button
        type="button"
        className={`axiomos-mobile-nav__backdrop${isOpen ? " is-visible" : ""}`}
        aria-label="Close navigation menu"
        aria-hidden={!isOpen}
        tabIndex={-1}
        onClick={() => closeMenu(true)}
      />

      <aside
        id={menuId}
        ref={menuRef}
        className={`axiomos-mobile-nav__drawer${isOpen ? " is-open" : ""}`}
        aria-labelledby={`${menuId}-title`}
        aria-hidden={!isOpen}
        aria-modal={isOpen ? "true" : undefined}
        role="dialog"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <header className="axiomos-mobile-nav__drawer-header">
          <div>
            <span className="axiomos-mobile-nav__eyebrow">AX / Navigation</span>
            <h2 id={`${menuId}-title`} className="axiomos-mobile-nav__title">
              Explore Axiomos
            </h2>
          </div>

          <button
            type="button"
            className="axiomos-mobile-nav__close"
            aria-label="Close navigation"
            tabIndex={isOpen ? 0 : -1}
            onClick={() => closeMenu(true)}
          >
            <FontAwesomeIcon icon={faTimes} aria-hidden="true" />
          </button>
        </header>

        <div className="axiomos-mobile-nav__search">
          <FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden="true" />
          <label
            htmlFor={`${menuId}-search`}
            className="axiomos-mobile-nav__sr-only"
          >
            Search navigation
          </label>
          <input
            ref={searchInputRef}
            id={`${menuId}-search`}
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setExpandedMenu(null);
            }}
            placeholder="Search services, industries, and more"
            autoComplete="off"
            tabIndex={isOpen ? 0 : -1}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              tabIndex={isOpen ? 0 : -1}
            >
              Clear
            </button>
          )}
        </div>

        <div className="axiomos-mobile-nav__drawer-body">
          {filteredItems.length > 0 ? (
            <ul className="axiomos-mobile-nav__list">
              {filteredItems.map((item, index) => {
                const hasChildren = Boolean(item.children?.length);
                const isSubmenuOpen =
                  expandedMenu === item.label || Boolean(query);
                const submenuId = `${menuId}-${item.label
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`;

                return (
                  <li
                    key={item.label}
                    className={`axiomos-mobile-nav__item${
                      isSubmenuOpen ? " is-expanded" : ""
                    }`}
                  >
                    <div className="axiomos-mobile-nav__item-row">
                      <span
                        className="axiomos-mobile-nav__item-index"
                        aria-hidden="true"
                      >
                        {String(index + 1).padStart(2, "0")}
                      </span>

                      <NavLink
                        to={item.to}
                        tabIndex={isOpen ? 0 : -1}
                        onClick={() => handleNavigation(item.to)}
                        className={({ isActive }) =>
                          [
                            "axiomos-mobile-nav__primary-link",
                            isActive ? "is-active" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")
                        }
                      >
                        <span className="axiomos-mobile-nav__link-content">
                          <strong>{item.label}</strong>
                          <small>{item.description}</small>
                        </span>
                      </NavLink>

                      {hasChildren ? (
                        <button
                          type="button"
                          className="axiomos-mobile-nav__submenu-toggle"
                          aria-label={`${
                            isSubmenuOpen ? "Collapse" : "Expand"
                          } ${item.label} submenu`}
                          aria-expanded={isSubmenuOpen}
                          aria-controls={submenuId}
                          tabIndex={isOpen ? 0 : -1}
                          disabled={Boolean(query)}
                          onClick={() => toggleSubmenu(item.label)}
                        >
                          <FontAwesomeIcon
                            icon={faChevronDown}
                            aria-hidden="true"
                          />
                        </button>
                      ) : (
                        <FontAwesomeIcon
                          icon={faArrowRight}
                          className="axiomos-mobile-nav__item-arrow"
                          aria-hidden="true"
                        />
                      )}
                    </div>

                    {hasChildren && (
                      <div
                        id={submenuId}
                        className={`axiomos-mobile-nav__submenu${
                          isSubmenuOpen ? " is-open" : ""
                        }`}
                        aria-hidden={!isSubmenuOpen}
                      >
                        <div className="axiomos-mobile-nav__submenu-inner">
                          <ul className="axiomos-mobile-nav__submenu-list">
                            {item.children.map((child) => {
                              const isChildActive =
                                currentLocation === child.to;

                              return (
                                <li
                                  key={`${item.label}-${child.to}-${child.label}`}
                                >
                                  <RouterLink
                                    to={child.to}
                                    tabIndex={isOpen && isSubmenuOpen ? 0 : -1}
                                    aria-current={
                                      isChildActive ? "page" : undefined
                                    }
                                    className={`axiomos-mobile-nav__submenu-link${
                                      isChildActive ? " is-active" : ""
                                    }`}
                                    onClick={() => handleNavigation(child.to)}
                                  >
                                    <span
                                      className="axiomos-mobile-nav__submenu-dot"
                                      aria-hidden="true"
                                    />
                                    <span>{child.label}</span>
                                    <FontAwesomeIcon
                                      icon={faArrowRight}
                                      aria-hidden="true"
                                    />
                                  </RouterLink>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="axiomos-mobile-nav__empty" role="status">
              <strong>No matching pages</strong>
              <p>Try a broader keyword or speak with our team.</p>
              <RouterLink
                to="/contact"
                tabIndex={isOpen ? 0 : -1}
                onClick={() => handleNavigation("/contact")}
              >
                Contact Axiomos
                <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </RouterLink>
            </div>
          )}
        </div>

        <footer className="axiomos-mobile-nav__footer">
          <div>
            <span>Start a conversation</span>
            <p>Have a product or software idea?</p>
          </div>

          <RouterLink
            to="/contact"
            className="axiomos-mobile-nav__cta"
            tabIndex={isOpen ? 0 : -1}
            onClick={() => handleNavigation("/contact")}
          >
            Discuss your project
            <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
          </RouterLink>
        </footer>
      </aside>
    </nav>
  );
};

export default MobileNav;
